// @ts-nocheck
import { createContext, useContext, useEffect, useState } from 'react';
import { Proposal } from '../models/Proposal';
import ProposalService from '../services/ProposalService';
import { tableColumnStatus } from '../utils/table';

// CONSTANTS
import { pageSizes } from '../constants/grid';

// ENUMS
import { Modals } from '../enums/portals.enum';

// CONTEXT
import { usePortals } from '../hooks/PortalsContext';
import { normalizeProposal } from '../utils/proposal';
import { isInss } from '../utils/consigneeRules';
import { Status } from '../enums/proposal-status.enum';
import { useNavigate } from 'react-router-dom';
import { numberFormat } from '../utils/formatCurrency';
import { useToast } from '../hooks/ToastContext';

export const ProposalContext = createContext({});
export const useProposal = (): any => useContext(ProposalContext);

const getPageSize = (pageSize = 10) => pageSizes.filter((size) => size.value == pageSize);

const ProposalProvider = ({ children }: any) => {
  const { handleOpenPortal, handleClosePortal } = usePortals();
  const [formOpened, setFormOpened] = useState(new Proposal());
  const [cnpjconsignees, setCnpjconsignees] = useState([]);
  const [stepLoading, setStepLoading] = useState(false);
  const [nextStepDisabled, setNextStepDisabled] = useState(false);
  const [prevStepDisabled, setPrevStepDisabled] = useState(false);
  const [pageSize, setPageSize] = useState(getPageSize());
  const [toast, setToast] = useState({
    opened: false,
    message: '',
  });
  const { addToast } = useToast();
  const [externalCpfError, setExternalCpfError] = useState('');
  const [tableFilters, setTableFilters] = useState({});
  const [tableConfiguration, setTableConfiguration] = useState(tableColumnStatus);
  const [tableSearch, setTableSearch] = useState('');
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const isConsignCardPendence =
    formOpened?.status === Status.PENDING_PURCHASE_MARGIN ||
    formOpened?.status === Status.PENDING_PURCHASE_MARGIN_WRONG_PROPOSAL;

  const changeTableConfiguration = (items: any) => {
    setTableConfiguration((prevItems) => [...prevItems, items]);
  };

  const clearForm = () => {
    setFormOpened(new Proposal());
    setStep(1);
  };

  const verifyPendenceOnProposalDocuments = (proposal: Proposal): boolean => {
    if (proposal.loanDetails.length > 1) {
      const hasPendence = proposal.loanDetails.some((loanDetail) => {
        return loanDetail.documentsProposal.some((document) => {
          if (document.status === 'true' || document.status === 'INVALID_DOCUMENT') {
            return true;
          }
        });
      });
      if (hasPendence) {
        handleOpenPortal(Modals.MODALPENDENCEDOCUMENTS);
        return false;
      }
    }
    return true;
  };

  const handleStep1 = async () => {
    setStep((previousStep: any) => previousStep + 1);
    setStepLoading(false);
  };

  const handleStep2 = async () => {
    setStepLoading(true);
    setPrevStepDisabled(true);
    const fetchData = await ProposalService.createProposal(normalizeProposal(formOpened));
    if (fetchData === undefined) {
      handleOpenPortal(Modals.ERRORPROPOSALGENERIC);
    } else {
      const { response } = fetchData;
      if (response != null) {
        if (response.status === 400 && response.data.validationErrors === undefined) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          return false;
        } else if (response.status === 400 && response.data.validationErrors.invalidProposalNumber) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          handleOpenPortal(Modals.ERRORPROPOSAL);
        } else if (response.data.validationErrors.invalidDocument || response.data.validationErrors.Consignee) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          handleOpenPortal(Modals.ERRORPROPOSAL);
        } else if (response.data.validationErrors.documentAlreadyRegistered) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          handleOpenPortal(Modals.ERRORPROPOSALINCONSISTENT);
        } else if (response.data.validationErrors.proposalRefused) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          handleOpenPortal(Modals.ANALYSISPROPOSAL);
        } else if (response.data.validationErrors.invalidConsigneeId) {
          setStepLoading(false);
          setPrevStepDisabled(false);
          addToast({
            type: 'error',
            title: 'Convênio selecionado é diferente do tipo do cartão.',
          });
          return false;
        }
        setStepLoading(false);
        setPrevStepDisabled(false);
        return false;
      }

      const shouldNavigateToManageProposal =
        formOpened.status === Status.PENDING_PURCHASE_MARGIN ||
        formOpened.status === Status.PENDING_PURCHASE_MARGIN_WRONG_PROPOSAL;

      if (shouldNavigateToManageProposal) {
        setStepLoading(false);
        setPrevStepDisabled(false);
        navigate('/manage-proposal');
        return false;
      }

      setStepLoading(false);
      setPrevStepDisabled(false);
      setFormOpened({
        ...formOpened,
        ...fetchData.data,
        loanDetails: formOpened.loanDetails,
      });
      setStep((previousStep: any) => previousStep + 1);
      if (!fetchData) return;
    }
  };

  const verifyMarginLimit = (proposal: Proposal): MarginLimitResult => {
    const PURCHASE_MARGIN_LIMIT = 20_000.0;
    const maxAllowedValue = PURCHASE_MARGIN_LIMIT / proposal.limitMultiply;
    const calculatedMargin = proposal.purchaseMargin * proposal.limitMultiply;

    if (calculatedMargin > PURCHASE_MARGIN_LIMIT) {
      return {
        isPurchaseMarginLimitValid: false,
        message: `Valor informado de margem para compras maior que ${numberFormat(maxAllowedValue)}, 
                  isso resulta em um limite final de ${numberFormat(calculatedMargin)}.`,
      };
    }

    return {
      isPurchaseMarginLimitValid: true,
      message: '',
    };
  };

  const validateAndExecuteMarginLimit = async (actionToExecute: () => void): Promise<void> => {
    const { isPurchaseMarginLimitValid, message } = verifyMarginLimit(formOpened);

    if (isPurchaseMarginLimitValid) {
      await actionToExecute();
    } else {
      handleOpenPortal(Modals.MODALPURCHASEMARGINLIMIT, {
        message,
        onClose: handleClosePortal,
        onConfirm: async () => {
          handleClosePortal();
          await actionToExecute();
        },
      });
    }
  };

  const isINSS = (proposal: Proposal) => isInss(proposal.cnpjConsigneeId);

  const verifyConsignCard = (proposal: Proposal): boolean => {
    if (proposal.hasConsignCard) {
      if (proposal.loanDetails.some((loanDetail) => loanDetail.purchaseMargin > 0)) return true;
      return false;
    }
    return true;
  };

  const prevStep = () => {
    setStep((previousStep: any) => previousStep - 1);
  };

  const nextStep = (e: any) => {
    e.preventDefault();
    if (step === 1) {
      setStepLoading(true);
      handleStep1();
    }
    if (step === 2) {
      setStepLoading(true);
      if (verifyPendenceOnProposalDocuments(formOpened)) {
        if (isINSS(formOpened)) {
          handleStep2();
        } else {
          if (verifyConsignCard(formOpened)) {
            handleStep2();
          } else {
            handleOpenPortal(Modals.MODALPURCHASEMARGIN, {
              onConfirm: async () => {
                handleClosePortal();
                await handleStep2();
              },
            });
            setStepLoading(false);
          }
        }
      } else {
        setStepLoading(false);
      }
    }
    if (step === 3) {
      setStep((previousStep: any) => previousStep + 1);
    }
    if (step === 4) {
    }
  };

  const values = {
    formOpened,
    changeTableConfiguration,
    setFormOpened,
    stepLoading,
    setStepLoading,
    toast,
    setToast,
    externalCpfError,
    setExternalCpfError,
    nextStepDisabled,
    setNextStepDisabled,
    prevStepDisabled,
    setPrevStepDisabled,
    step,
    setStep,
    prevStep,
    nextStep,
    tableConfiguration,
    setPageSize,
    pageSize,
    getPageSize,
    tableFilters,
    setTableFilters,
    tableSearch,
    setTableSearch,
    cnpjconsignees,
    setCnpjconsignees,
    clearForm,
    isConsignCardPendence,
    validateAndExecuteMarginLimit,
  };

  return <ProposalContext.Provider value={{ ...values }}>{children}</ProposalContext.Provider>;
};

export { ProposalProvider };
